import { confirmSignIn, getCurrentUser, signIn, signOut } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthLayout } from '../components/AuthLayout';

/**
 * Amplify Auth のトークンストアからトークンを取得してアプリにリダイレクトする
 * Amplify v6 の内部 API を使用してリフレッシュトークンを含む全トークンを取得
 */
async function redirectToAppWithTokens(): Promise<void> {
  try {
    // Amplify の内部トークンストアから全トークンを取得
    const tokens = await cognitoUserPoolsTokenProvider.tokenOrchestrator
      ?.getTokenStore()
      .loadTokens();

    if (!tokens) {
      throw new Error('トークンを取得できませんでした');
    }

    const idToken = tokens.idToken?.toString();
    const accessToken = tokens.accessToken?.toString();
    const refreshToken = tokens.refreshToken?.toString();

    // デバッグログ
    console.log('[SignInPage] トークン取得結果:', {
      hasIdToken: !!idToken,
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      refreshTokenLength: refreshToken?.length || 0,
    });

    if (!idToken || !accessToken) {
      throw new Error('IDトークンまたはアクセストークンが見つかりません');
    }

    // 保存されたstateを取得
    const state = sessionStorage.getItem('auth_state');

    // トークンをCallbackページに渡す
    const params = new URLSearchParams();
    params.set('id_token', idToken);
    params.set('access_token', accessToken);
    if (refreshToken) params.set('refresh_token', refreshToken);
    if (state) params.set('state', state);

    // Callbackページに遷移（Callbackページでアプリにリダイレクト＆完了画面表示）
    window.location.href = `/auth/callback?${params.toString()}`;
  } catch (err) {
    console.error('トークン取得エラー:', err);
    throw err;
  }
}

export function SignInPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [challengeName, setChallengeName] = useState<string | null>(null);

  // 初期化時に既存セッションをクリア
  useEffect(() => {
    const clearExistingSession = async () => {
      try {
        // 既存ユーザーがいるか確認
        await getCurrentUser();
        // 既存セッションがある場合はサインアウト
        await signOut();
      } catch {
        // ユーザーがいない場合は何もしない
      } finally {
        setInitializing(false);
      }
    };
    clearExistingSession();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await signIn({
        username: email,
        password,
      });

      if (result.nextStep.signInStep === 'DONE') {
        // サインイン成功後、Amplifyのトークンストアからトークンを取得してアプリにリダイレクト
        await redirectToAppWithTokens();
        return; // リダイレクト後は処理を終了
      } else if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setChallengeName('NEW_PASSWORD_REQUIRED');
      } else {
        setError(`追加の認証ステップが必要です: ${result.nextStep.signInStep}`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : '認証に失敗しました';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await confirmSignIn({
        challengeResponse: newPassword,
      });

      if (result.nextStep.signInStep === 'DONE') {
        // 新パスワード設定成功後、Amplifyのトークンストアからトークンを取得してアプリにリダイレクト
        await redirectToAppWithTokens();
        return; // リダイレクト後は処理を終了
      } else {
        setError(`追加の認証ステップが必要です: ${result.nextStep.signInStep}`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : '新しいパスワードの設定に失敗しました';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // 初期化中はローディング表示
  if (initializing) {
    return (
      <AuthLayout title="サインイン" subtitle="準備中...">
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-4 border-muted border-l-primary-500 rounded-full animate-spin" />
        </div>
      </AuthLayout>
    );
  }

  if (challengeName === 'NEW_PASSWORD_REQUIRED') {
    return (
      <AuthLayout
        title="新しいパスワードを設定"
        subtitle="初回ログインのため、新しいパスワードを設定してください"
      >
        <form onSubmit={handleNewPassword} className="space-y-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-foreground mb-2">
              新しいパスワード
            </label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              className="w-full px-4 py-3 bg-muted/50 border border-input rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="新しいパスワードを入力"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-4 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? '設定中...' : 'パスワードを設定'}
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="サインイン" subtitle="きゅーもAI エージェント にサインイン">
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
            メールアドレス
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-3 bg-muted/50 border border-input rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="example@email.com"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
            パスワード
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full px-4 py-3 bg-muted/50 border border-input rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="パスワードを入力"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 px-4 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'サインイン中...' : 'サインイン'}
        </button>
      </form>

      <div className="mt-6 space-y-3">
        <Link
          to="/auth/forgot-password"
          className="block text-center text-sm text-primary-400 hover:text-primary-300 transition-colors"
        >
          パスワードをお忘れですか？
        </Link>
        <Link
          to="/auth/signup"
          className="block text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          アカウントをお持ちでない方は
          <span className="text-primary-400">サインアップ</span>
        </Link>
      </div>
    </AuthLayout>
  );
}
